import { useGenericSocket } from "@/hooks/useGenericSocket.ts";
import { SocketTopics } from "@/lib/natsConnection.ts";
import { useCallback } from "react";

export interface IMarginLevelWindowRespone {
  timestamp: number;
  chanel: string;
  data: IMarginLevelDetails[];
}

export interface IMarginLevelDetails {
  symbol: string;
  timestamp: number;
  side: string;
  price: number;
  weight: string;
  quoteQuantity: number;
  prc: number;
  quoteQuantityCum: number;
  index: number;
}

export interface IMarginLevelBalance {
  symbol: string;
  buyLevelsCount: number;
  sellLevelsCount: number;
  buyVolumeSum: number;
  sellVolumeSum: number;
  prc: number;
}

export function useMarginLevelWindow() {
  const handler = useCallback(
    (_: IMarginLevelBalance[], newData: IMarginLevelWindowRespone) => {
      if (!newData.data) {
        return [];
      }

      // Group data by symbol
      const groupedBySymbol = newData.data.reduce(
        (acc, item) => {
          if (!acc[item.symbol]) {
            acc[item.symbol] = {
              buyLevels: 0,
              sellLevels: 0,
              buyVolume: 0,
              sellVolume: 0,
              prc: item.prc, // Initialize with first price
            };
          }

          if (item.side === "UP") {
            acc[item.symbol].buyLevels++;
            acc[item.symbol].buyVolume += item.quoteQuantity;
            // Update prc only if it's lower than current
            acc[item.symbol].prc = Math.min(acc[item.symbol].prc, item.prc);
          } else {
            acc[item.symbol].sellLevels++;
            acc[item.symbol].sellVolume += item.quoteQuantity;
            // Update prc only if it's lower than current
            acc[item.symbol].prc = Math.min(acc[item.symbol].prc, item.prc);
          }

          return acc;
        },
        {} as Record<
          string,
          {
            prc: number;
            buyLevels: number;
            sellLevels: number;
            buyVolume: number;
            sellVolume: number;
          }
        >,
      );

      // Convert to IMarginLevelBalance array
      return Object.entries(groupedBySymbol)
        .map(
          ([symbol, data]): IMarginLevelBalance => ({
            symbol,
            buyLevelsCount: data.buyLevels,
            sellLevelsCount: data.sellLevels,
            buyVolumeSum: data.buyVolume,
            sellVolumeSum: data.sellVolume,
            prc: data.prc,
          }),
        )
        .sort((a, b) => b.buyLevelsCount - a.buyLevelsCount);
    },
    [],
  );

  return useGenericSocket<IMarginLevelBalance[], IMarginLevelWindowRespone>(
    SocketTopics.predictLiquidationWindow,
    handler,
    { name: "predictLiquidationWindow" },
  );
}
